import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { Buildings, X } from "@phosphor-icons/react";

/**
 * Public, no-auth employer autocomplete for donation forms.
 * Calls /api/matching/employers/search?q= and surfaces matching-gift policy when one is picked.
 */
export default function EmployerSearch({ value, onChange }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [policy, setPolicy] = useState(null);
    const timer = useRef(null);

    useEffect(() => {
        if (value?.id && !policy) {
            axios.get(`${API}/matching/employers/${value.id}`).then(r => setPolicy(r.data)).catch(() => {});
        }
    }, [value, policy]);

    const onType = (s) => {
        setQ(s);
        if (timer.current) clearTimeout(timer.current);
        if (!s || s.length < 2) { setResults([]); return; }
        timer.current = setTimeout(async () => {
            try {
                const r = await axios.get(`${API}/matching/employers/search`, { params: { q: s } });
                setResults(r.data);
                setOpen(true);
            } catch { setResults([]); }
        }, 200);
    };

    const pick = (e) => {
        onChange({ id: e.id, name: e.name });
        setQ(e.name);
        setOpen(false);
        setPolicy(null);
    };

    const clear = () => {
        onChange(null);
        setQ("");
        setResults([]);
        setPolicy(null);
    };

    return (
        <div className="space-y-2" data-testid="employer-search">
            <label className="label-eyebrow text-xs text-foreground/70">Your employer (optional — many companies match gifts!)</label>
            <div className="relative">
                <div className="flex items-center border border-border">
                    <Buildings size={16} className="mx-3 text-foreground/40"/>
                    <input
                        value={q || value?.name || ""}
                        onChange={e => onType(e.target.value)}
                        onFocus={() => results.length && setOpen(true)}
                        placeholder="Search for your company…"
                        className="flex-1 px-2 py-2 outline-none text-sm"
                        data-testid="employer-input"
                    />
                    {(value || q) && (
                        <button type="button" onClick={clear} className="px-3 text-foreground/40 hover:text-destructive" data-testid="employer-clear"><X size={14}/></button>
                    )}
                </div>
                {open && results.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 bg-white border border-border shadow-lg max-h-64 overflow-y-auto" data-testid="employer-suggestions">
                        {results.map(r => (
                            <button type="button" key={r.id} onClick={() => pick(r)} className="block w-full text-left px-3 py-2 text-sm hover:bg-secondary/40 border-b border-border last:border-b-0" data-testid={`emp-opt-${r.id}`}>
                                <span className="font-medium">{r.name}</span>
                                {r.has_matching_program && r.match_ratio && (
                                    <span className="text-[10px] uppercase tracking-wider text-accent ml-2 font-bold">Matches {r.match_ratio}:1</span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {value && policy && policy.has_matching_program && (
                <div className="bg-accent/10 border border-accent/30 p-3 text-xs" data-testid="employer-policy">
                    <p className="font-bold text-accent">🎉 {policy.name} matches gifts at {policy.match_ratio}:1 — up to ${(policy.max_amount || 0).toLocaleString()}/year.</p>
                    <p className="text-foreground/70 mt-1">After donating, we'll email you a link to submit your match request{policy.deadline_desc ? ` (${policy.deadline_desc})` : ""}.</p>
                </div>
            )}
        </div>
    );
}
